import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import Icon, {
  CloseOutlined,
  DownloadOutlined,
  ExclamationCircleFilled,
  LoadingOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SyncOutlined,
  ReloadOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import {
  Badge,
  Button,
  Card,
  message,
  PaginationProps,
  Space,
  Table,
  Input,
  Modal,
  Typography,
  Divider,
  Checkbox,
  Alert,
  Tooltip,
  Popover,
  Radio,
  Dropdown,
  Menu,
} from 'antd';
import { ColumnProps } from 'antd/lib/table';
import dayjs from 'lib/dayjs';
import {
  AdminStore,
  AeAdminStore,
  deleteStores,
  exportStoreList,
  getAdminStores,
  GetAdminStoresParams,
  getAeAdminStores,
  getAliExpressAuthUrl,
  getLastAuthInfo,
  getStoreStatusCount,
  NeedToUpdateStore,
  removeShopStore,
  syncStoreInfo,
  updateStoreAlias,
} from 'services/api/admin';
import chatttSDK, { multiAuthSupportSelectDepartment } from 'services/chattt';
import { useAuthorization } from 'components/Authorization';
import { ShopeeMultiAuthShop } from 'services/api/uploads';
import useShopeeAuthShops, {
  AuthMode,
  AuthShopsStatus,
  authShopsStatus,
} from 'hooks/business/authShops/useShopeeAuthShops';
import * as xlsx from 'xlsx';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import useShops from 'hooks/business/authShops/useShops';
import { compare } from 'compare-versions';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import { regionLabelMap } from 'services/config/region';
import useAuthShopUtils from 'hooks/business/authShops/useShopeeUtils';
import useModal from 'hooks/useModal';
import { enableWa, getWabaAccountCount } from 'services/api/whatsapp';
import { useSetInvalidAccount } from 'hooks/business/account';
import {
  useRegionCountsRefresher,
  useRegionStoreCountOfPlatform,
} from 'hooks/business/store/useRegionStoreCount';
import ShopeeAuthShopDialog from 'components/AuthShop/ShopeeDialog';
import LazadaAuthShopDialog from 'components/AuthShop/LazadaDialog';
import TiktokAuthShopDialog from 'components/AuthShop/TiktokDialog';
import MercadoAuthShopDialog from 'components/AuthShop/MercadoDialog';
import { useIsFinishNewbieAuth } from 'hooks/business/user/useNewbieAuthGuide';
import { useIsOverOneAssistantState } from 'hooks/business/isOnlyOneAssistant';
import { useGlobalQuery, useSetGlobalQuery } from 'hooks/business/useGlobalQuery';
import { SortOrder } from 'antd/es/table/interface';
import { useQuery as useRouterQuery, simpleStringify } from 'components/RouterQuery';
import StatusButton from 'components/StatusButton';
import useAuthShopErrorCount from 'hooks/business/authShops/useAuthShopErrorCount';
import { useGetPermission, useRouteAccessProps } from 'hooks/business/useNewPermisson';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useLatest, useMemoizedFn, useMount, useRequest, useUpdateEffect } from 'ahooks';
import { useNavigate } from 'react-router-dom';
import RegionSelector from 'views/Aide/components/RegionSelector';
import { isAeProduct, isMasterOnline, isSiteGiant } from 'productLine';
import PlatformSelector, { PlatformSelectorProps } from 'views/Aide/components/PlatformSelector';
import { openExternalURL } from 'utils/openExternalURL';

import { useOpenReturnStatus } from 'hooks/business/openReturnStatus';
import { What } from 'views/Marketing/components/PolicyTable';
import InlineEditor from './InlineEditor';

import StoreTypeModal from './ShopeeAuth/StoreTypeModal';
import MultiAuthInterruptModal from './ShopeeAuth/MultiAuth/MultiAuthInterruptModal';
import LazadaAuthModal, { LazadaAuthModalRefObject } from './LazadaAuth/LazadaAuthModal';
import ShopAliasSetting from './AliasSetting';
import SingleAuthResultModal from './ShopeeAuth/SingleAuthResultModal';
import AuthCaptchaModal from './ShopeeAuth/AuthCaptchaModal';
import MultipleAuthProgressModal from './ShopeeAuth/MultipleAuthProgressModal';
import MultipleAuthResultModal from './ShopeeAuth/MultipleAuthResultModal';
import BatchUpdateAliasModal from './BatchUpdateAliasModal';
import BatchUpdateAccountInfo from './UpdateAccount/Tip';
import UpdateAccountModal from './UpdateAccount/Modal';
import WhatsappList from './Whatsapp/List';
import TokopediaEmpty from './Tokopedia/TokopediaEmpty';
import EditShopModal from './Tokopedia/EditShopModal';
import { TokoRegForm2Value } from './Tokopedia/TokoRegForm2';
import withSuspense from 'components/withSuspense';
import { useTransAppProductName } from 'hooks/useTrans';
import { AuthBeAboutToExpireFeature } from 'utils/featureFlag';
import { useSearchParams } from 'react-router-dom';
import { useTikTokUSEnable } from 'hooks/business/store/useTikTokUSEnable';
import { isAuthStatusValid } from 'utils/is';
import { refreshInProcessState } from '../../BootGuide/atoms/inProcess';
import { useExportFile } from 'components/ExportCenter/hooks/useExportCenter';
import { useStoreNameConfig } from 'hooks/business/store/useStoreName';
import { useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { noop } from 'lodash-es';
import nimInst from '../../../services/nim';
import { ReactComponent as AliasSettingIcon } from './assets/aliassetting.svg';
import { useDetectClientModal } from 'components/DetectClientModal/hooks';
import { LazadaMultiAuthModal } from './LazadaAuth/MultiAuth';
import KwaiAuthShopDialog from 'components/AuthShop/KwaiDialog';
import JolectAuthShopDialog from 'components/AuthShop/JolectAuthShopDialog';
import {
  getShopeeLocalMultipleAuthTemplateFile,
  getShopeeCrossMultipleAuthTemplateFile,
} from 'services/i18n/utils';
import GuideDialog from 'components/AuthShop/NewbieGuideDialog';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { ApiQueryKey } from 'services/config/queryKey';

const { Text } = Typography;

export interface StoresProps {}

const DEFAULT_PAGE_SIZE = 10;
const Stores = (_props: StoresProps) => {
  const { token, payload } = useAuthorization<Oversea.Token.Payload>();
  const [searchParams] = useSearchParams();
  const isFinishNewbieAuth = useIsFinishNewbieAuth();
  const globalQuery = useGlobalQuery();
  const setGlobalQuery = useSetGlobalQuery();
  const { tabName, listAuthStatus, all } = useRouterQuery();
  const queryPlatform = isAeProduct ? 'ALIEXPRESS' : tabName || globalQuery.platform;
  const [platform, setPlatform] = useState<General.PlatformUpperCaseTunnel>(
    queryPlatform?.toUpperCase?.() || 'SHOPEE',
  );
  const platformRef = useLatest(platform);
  const authStatusFromQuery = Number(searchParams.get('authStatus')) as 1 | 2;

  const systemLanguage = useSystemLanguageValue();

  const { whiteListConfig: { SHOPEE_BATCH_AUTH = false } = {} } = useOrgConfigStateValue();

  const { data: wabaCount } = useRequest(getWabaAccountCount);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [showRPAProgressModal, setShowRPAProgreessModal] = useState(false);
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const appProductNameT = useTransAppProductName();

  // const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState<AdminStore[]>([]);
  const [currentRegion, setCurrentRegion] = useState<General.UpperCaseRegion | undefined>(
    all === '1' ? undefined : globalQuery.region,
  );
  const currentRegionRef = useLatest(currentRegion);
  useUpdateEffect(() => {
    batchedUpdates(() => {
      setPlatform(queryPlatform?.toUpperCase?.() || 'SHOPEE');
      setCurrentRegion(undefined);
    });
    setTimeout(() => {
      refreshTable(1, Number(authStatusFromQuery));
    });
  }, [queryPlatform]);
  const shopeeAuthDataRef = useRef<{
    authMode: AuthMode;
    storeType: IM_API.Admin.StoreType;
    partnerType?: IM_API.Admin.PartnerType;
    region: General.CaselessRegion | 'WORLD';
    multiShops: ShopeeMultiAuthShop[];
    remember?: boolean;
    departmentIds?: number[];
    proxyMode?: boolean;
  }>({
    authMode: AuthMode.Single,
    storeType: 'LOCAL',
    // partnerType: 'CHAT',
    region: 'WORLD',
    multiShops: [],
    departmentIds: [],
    proxyMode: true,
  });
  const lazadaAuthModalRef = useRef<LazadaAuthModalRefObject>({} as any);
  const [pagination, setPagination] = useState<PaginationProps>({
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
    current: 1,
  });
  const [keyword, setKeyword] = useState('');
  const [authStatusParam, setAuthStatusParam] = useState<-1 | 1 | 2 | 3>(-1);
  const authStatusParamRef = useLatest(authStatusParam);
  const {
    setShopeeAuthShopsTaskId,
    setShopeeAuthShopsMode,
    storeTypeModalVisible,
    showStoreTypeModal,
    hideStoreTypeModal,
    setIsQuickMode,
    reset,
    isMultipleAuthResultCompleted,
  } = useShopeeAuthShops();
  const authStatus = useAtomValue(authShopsStatus);
  const { deleteStoreById } = useShops();
  const { getAuthDescription, canQuickMode } = useAuthShopUtils();

  const {
    visible: editTokoShopModalVisible,
    meta: editTokoShopModalMeta,
    update: updateTokoShopModalVisible,
  } = useModal<{ initialValues: Partial<TokoRegForm2Value> }>(false);

  const { authProxyDisabledList } = useFlagConfigValue();
  const { run: onDownload, loading: exportStoreListLoading } = useExportFile();
  const { meta, update, hide, visible } = useModal<NeedToUpdateStore[]>(false);
  const refreshInvalidAccountList = useSetInvalidAccount();

  const [lazadaAuthShopDialogVisible, setLazadaAuthShopDialogVisible] = useState(false);
  const [tiktokAuthShopDialogVisible, setTiktokAuthShopDialogVisible] = useState(false);
  const [mercadoAuthShopDialogVisible, setMercadoAuthShopDialogVisible] = useState(false);
  const [kwaiAuthDialogVisible, setKwaiAuthDialogVisible] = useState(false);
  const [facebookDialogVisible, setFacebookDialogVisible] = useState(false);
  const [jolectAuthDialogVisible, setJolectAuthDialogVisible] = useState(false);

  const [shopeeAuthShopDialogVisible, setShopeeAuthShopDialogVisible] = useState(false);
  const [shopeeAuthPartnerType, setShopeeAuthPartnerType] = useState<{
    original: boolean;
    shopName: string;
    chat: boolean;
    erp: boolean;
  }>({ original: true, shopName: '', chat: true, erp: true });

  const [shopeeInitialValues, setShopeeInitialValues] = useState<{
    storeType?: IM_API.Admin.StoreType;
    region?: General.UpperCaseRegion | 'CNSC';
    departmentIds?: number[];
  }>();

  const [guideInitialValues, setGuideInitialValues] = useState<{
    storeType?: IM_API.Admin.StoreType;
    region?: General.UpperCaseRegion;
    departmentIds?: number[];
  }>();

  const { detectClient } = useDetectClientModal();
  // 批量删除 id 列表
  const [selectedDeleteKeys, setSelectedDeleteKeys] = useState<React.Key[]>([]);

  // shopee 更新账号白名单
  const openReturn = useOpenReturnStatus();

  const [queryAdminStores, setQueryAdminStores] = useState<GetAdminStoresParams | undefined>();

  const tiktokTimerRef = useRef<NodeJS.Timeout>();
  useEffect(() => {
    if (tiktokTimerRef.current) {
      clearInterval(tiktokTimerRef.current);
    }
  }, []);

  const isOverOneAssistant = useIsOverOneAssistantState();
  const { errorCount, getErrorCount } = useAuthShopErrorCount();
  const nav = useNavigate();
  const { data: waEnable } = useRequest(enableWa);
  const [mounted, setMounted] = useState(false);
  const getPermission = useGetPermission();
  const tiktokUSEnable = useTikTokUSEnable();

  const { newPermissionEnable } = useRouteAccessProps();

  const listAuthStatusComputed = useMemo(
    () => (listAuthStatus && parseInt(listAuthStatus, 10) > 0 ? 1 : undefined),
    [listAuthStatus],
  );

  const { data: countInfo, refetch: refresh } = useQuery({
    queryKey: [ApiQueryKey.StorePlatformRegionStatusCount, platform, currentRegion],
    queryFn: () => getStoreStatusCount(platform, currentRegion!),
  });

  const { data: adminStoresData, isLoading: loading } = useQuery({
    enabled: !!queryAdminStores,
    queryKey: [ApiQueryKey.AdminStores, queryAdminStores],
    queryFn: async () => {
      if (queryAdminStores?.externalType === 'ALIEXPRESS') {
        const { infos, total } = await getAeAdminStores(queryAdminStores);
        return {
          stores: infos,
          total,
          pageSize: queryAdminStores.pageSize,
          page: queryAdminStores.page,
          authStatus: queryAdminStores.authStatus,
        };
      }

      const res = await getAdminStores(queryAdminStores!);
      return {
        ...res.data.data,
        pageSize: queryAdminStores!.pageSize,
        page: queryAdminStores!.page,
        authStatus: queryAdminStores!.authStatus,
      };
    },
  });

  const lazadaStoreCount = useRegionStoreCountOfPlatform('LAZADA');
  const shopeeStoreCount = useRegionStoreCountOfPlatform('SHOPEE');
  const tokopediaStoreCount = useRegionStoreCountOfPlatform('TOKOPEDIA');
  const tiktokStoreCount = useRegionStoreCountOfPlatform('TIKTOK');
  const aliexpressStoreCount = useRegionStoreCountOfPlatform('ALIEXPRESS');
  const tiktokUSStoreCount = useRegionStoreCountOfPlatform('TIKTOK_US');
  const mercadoStoreCount = useRegionStoreCountOfPlatform('MERCADO');
  const kwaiStoreCount = useRegionStoreCountOfPlatform('KWAI');
  const facebookStoreCount = useRegionStoreCountOfPlatform('FACEBOOK');
  const jolectStoreCount = useRegionStoreCountOfPlatform('JOLECT');

  const regionCountRefresher = useRegionCountsRefresher();

  const memorizedCount = useMemo(() => {
    return {
      SHOPEE: shopeeStoreCount,
      LAZADA: lazadaStoreCount,
      WA_BIZ: {
        total: wabaCount,
      },
      TOKOPEDIA: tokopediaStoreCount,
      TIKTOK: tiktokStoreCount,
      ALIEXPRESS: aliexpressStoreCount,
      TIKTOK_US: tiktokUSStoreCount,
      MERCADO: mercadoStoreCount,
      KWAI: kwaiStoreCount,
      FACEBOOK: facebookStoreCount,
      JOLECT: jolectStoreCount,
    };
  }, [
    lazadaStoreCount,
    shopeeStoreCount,
    wabaCount,
    tokopediaStoreCount,
    tiktokStoreCount,
    aliexpressStoreCount,
    tiktokUSStoreCount,
    mercadoStoreCount,
    kwaiStoreCount,
    facebookStoreCount,
    jolectStoreCount,
  ]);

  const { visible: lazadaMultiVisible, show: showLazadaMulti, hide: hideLazadaMulti } = useModal();

  const startPlatformAuth = useCallback(
    async (platform: General.Platform) => {
      if (platform === 'shopee') {
        if (chatttSDK && chatttSDK.app && chatttSDK.app.getVersion) {
          const version = await chatttSDK.app.getVersion();
          if (compare(version, '2.40.0', '>=')) {
            showStoreTypeModal();
            return;
          }
        }

        message.warning({
          content: <span>{t('升级至{version}', { version: '2.40.0' })}</span>,
          duration: 3,
        });
      } else {
        // 这里
        if (chatttSDK?.app.getVersion()) {
          const version = await chatttSDK.app.getVersion();
          if (compare(version, '2.33.0', '>=')) {
            showLazadaMulti();
            return;
          }
        }
        message.warning({
          content: <span>{t('升级至{version}', { version: '2.33.0' })}</span>,
          duration: 3,
        });
      }
    },
    [showStoreTypeModal, t, showLazadaMulti],
  );

  const handleReAuth = useCallback(
    async (record: AdminStore, chat = true, erp = true) => {
      let lastAuthInfo:
        | { storeType?: IM_API.Admin.StoreType; departmentIds?: number[] }
        | undefined;
      try {
        const res = await getLastAuthInfo(record.id);
        if (res) {
          lastAuthInfo = res;
        }
      } catch {
        // 静默失败
      }

      const { externalType, region, alias, storeName, storeType } = record;

      if (externalType === 'SHOPEE') {
        setShopeeAuthPartnerType({
          original: false,
          shopName: alias || storeName,
          chat,
          erp,
        });
        setShopeeInitialValues({
          storeType: lastAuthInfo?.storeType || storeType,
          region: region as General.UpperCaseRegion,
          departmentIds: lastAuthInfo?.departmentIds,
        });
        setShopeeAuthShopDialogVisible(true);
        return;
      }

      setGuideInitialValues({
        storeType: lastAuthInfo?.storeType || storeType,
        region: region as General.UpperCaseRegion,
        departmentIds: lastAuthInfo?.departmentIds,
      });

      switch (externalType) {
        case 'LAZADA':
          setLazadaAuthShopDialogVisible(true);
          break;
        case 'TIKTOK':
        case 'TIKTOK_US':
          setTiktokAuthShopDialogVisible(true);
          break;
        case 'MERCADO':
          setMercadoAuthShopDialogVisible(true);
          break;
        case 'KWAI':
          setKwaiAuthDialogVisible(true);
          break;
        case 'FACEBOOK':
          setFacebookDialogVisible(true);
          break;
        case 'JOLECT':
          setJolectAuthDialogVisible(true);
          break;
        default:
          break;
      }
    },
    [
      setShopeeAuthPartnerType,
      setShopeeInitialValues,
      setShopeeAuthShopDialogVisible,
      setGuideInitialValues,
      setLazadaAuthShopDialogVisible,
      setTiktokAuthShopDialogVisible,
      setMercadoAuthShopDialogVisible,
      setKwaiAuthDialogVisible,
      setFacebookDialogVisible,
    ],
  );

  const refreshTable = useCallback(
    (page = 1, authStatus?: 1 | 2 | 3 | -1, keyWord?: string) => {
      // eslint-disable-next-line no-console
      regionCountRefresher();
      getErrorCount();
      setKeyword(keyWord || '');
      setQueryAdminStores({
        keyWord,
        externalType: platformRef.current,
        region: currentRegionRef.current,
        pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
        page,
        authStatus: authStatus || listAuthStatusComputed,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getErrorCount,
      listAuthStatusComputed,
      pagination.pageSize,
      regionCountRefresher,
      setQueryAdminStores,
    ],
  );

  // 平台切换
  const changePlatform = useCallback(
    (key: string, resetRegion = true) => {
      const region = resetRegion ? undefined : currentRegion;
      setCurrentRegion(region);
      if (key !== 'WA_BIZ') {
        setGlobalQuery({
          platform: key as General.PlatformUpperCase,
          region,
        });
        setQueryAdminStores({
          externalType: key as General.PlatformUpperCase,
          region,
          pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
          page: 1,
          authStatus: listAuthStatusComputed,
        });
      }
      setKeyword('');
      setPlatform(key as General.PlatformUpperCase);
      nav({
        search: `?${simpleStringify({
          tabName: key,
          listAuthStatus,
        })}`,
      });
    },
    [
      listAuthStatus,
      listAuthStatusComputed,
      nav,
      currentRegion,
      pagination.pageSize,
      setQueryAdminStores,
      setGlobalQuery,
    ],
  );

  const refsCurrent = { changePlatform };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  // 渲染删除按钮
  const renderDeleteBtn = useMemoizedFn((id: number) => {
    return (
      <StatusButton
        type="link"
        style={{ paddingLeft: 0 }}
        onClick={() => {
          return removeShopStore({ shopId: id })
            .then((res) => {
              const {
                data: { code },
              } = res;

              if (code === 200) {
                message.success(t('ViewShops.col.actions.remove.success'));
                refreshTable(pagination.current);
                refresh();
                refreshInProcessState();
                deleteStoreById(id);
                setSelectedDeleteKeys((keys) => keys.filter((k) => k !== id));
              }
            })
            .catch((_err) => {
              message.error(t('ViewShops.col.actions.remove.fail'));
            });
        }}
        popConfirmProps={{
          title: appProductNameT('ViewShops.batchDeleteConfirmDetail'),
          overlayStyle: {
            maxWidth: 320,
          },
          placement: 'leftBottom',
        }}
      >
        {t('ViewShops.col.actions.remove')}
      </StatusButton>
    );
  });

  // 渲染更新账号按钮
  const renderUpdateAccountBtn = useMemoizedFn((record: AdminStore) => {
    const { id, authAccount, alias, storeName, region } = record;

    return (
      platform === 'SHOPEE' &&
      openReturn && (
        <Button
          onClick={() =>
            update(true, [
              {
                storeId: String(id),
                account: authAccount,
                password: '',
                storeName,
                storeAlias: alias,
                region,
              },
            ])
          }
          type="link"
        >
          {t('ViewShops.更新帐号')}
        </Button>
      )
    );
  });

  const { refresh: refreshStoreName } = useStoreNameConfig();
  const batchUpdateAliasRefresh = useCallback(() => {
    refreshTable();
    refreshStoreName();
  }, [refreshTable, refreshStoreName]);

  useEffect(() => {
    if (adminStoresData) {
      batchedUpdates(() => {
        const { stores, total, pageSize, page, authStatus } = adminStoresData;
        setStores(stores);
        setPagination({
          pageSize,
          current: page,
          total,
        });
        setAuthStatusParam(authStatus || -1);
      });
    }
  }, [adminStoresData]);

  const columns = useMemo(() => {
    const columns = [
      {
        title: t('ViewShops.col.store.name'),
        dataIndex: 'storeName',
        width: 160,
        fixed: 'left',
        className: styles.column,
      },
      {
        title: () => {
          return (
            <>
              {t('ViewShops.col.store.name.remark')}
              <Tooltip title={appProductNameT('ViewShops.shoppe.table.region.setting.tips')}>
                <QuestionCircleOutlined />
              </Tooltip>
            </>
          );
        },
        dataIndex: 'alias',
        width: 240,
        className: styles.column,
        render: (alias, record) => {
          return (
            <InlineEditor
              value={alias}
              key={alias}
              onSubmit={(value) => {
                return updateStoreAlias(record.id, value).then((resp) => {
                  refreshStoreName();
                  return resp;
                });
              }}
            />
          );
        },
      },
      { title: t('ViewShops.col.store.id'), dataIndex: 'externalId', width: 120 },
      { title: appProductNameT('ViewShops.col.store.chatId'), dataIndex: 'id', width: 120 },
    ] as ColumnProps<AdminStore>[];
    if (platform !== 'TOKOPEDIA' && platform !== 'ALIEXPRESS' && platform !== 'FACEBOOK') {
      columns.push({
        title: t('ViewShops.col.store.name.region'),
        dataIndex: 'region',
        width: 140,
        render: (region: General.UpperCaseRegion) => {
          return <LineText>{getRegionLabel(region)}</LineText>;
        },
      });
    }

    columns.push(
      ...([
        {
          title: t('ViewShops.col.auth.status'),
          dataIndex: 'chatAuthStatus',
          width: 180,
          render: (_, record) => {
            const {
              erpAuthStatus,
              chatAuthStatus,
              isStoreInitFinish,
              region,
              storeType,
              authStatus,
              authExceptionInfo: { missingCsatPermission, missingProductReviewPermission } = {},
            } = record;
            const missingPermission = missingCsatPermission || missingProductReviewPermission;

            if (
              authStatus === 'AUTH_BE_ABOUT_EXPIRE' &&
              !isAeProduct &&
              !(missingPermission && authStatusParam === 3)
            ) {
              return (
                <LineText>
                  <Badge color="var(--fett-surface-warning-text)" text={t('be_about_to_expire')} />
                </LineText>
              );
            }

            if (platform === 'ALIEXPRESS') {
              if (isAuthStatusValid(erpAuthStatus)) {
                return (
                  <LineText>
                    <Badge
                      color="var(--fett-scenario-success-text)"
                      text={t('ViewShops.col.auth.status.active')}
                    />
                  </LineText>
                );
              }

              return (
                <LineText>
                  <Badge
                    color="var(--fett-text-quaternary)"
                    text={t('ViewShops.col.auth.status.to.be.expire')}
                  />
                </LineText>
              );
            }

            if (erpAuthStatus === 'AUTH_PROCESS' || chatAuthStatus === 'AUTH_PROCESS') {
              return (
                <Space>
                  <LineText style={{ color: 'var(--ant-primary-color)' }}>
                    <SyncOutlined spin />
                    {t('ViewShops.col.auth.status.to.be.auth.pending')}
                  </LineText>
                </Space>
              );
            }

            if (erpAuthStatus === 'AUTH_EXPIRE' && chatAuthStatus === 'AUTH_EXPIRE') {
              return (
                <LineText>
                  <Badge
                    color="var(--fett-text-quaternary)"
                    text={t('ViewShops.col.auth.status.to.be.expire')}
                  />
                </LineText>
              );
            }

            if (chatAuthStatus === 'AUTH_EXPIRE' || chatAuthStatus === 'UN_AUTH') {
              return (
                <Space size={0} direction="vertical">
                  <LineText>
                    <Badge color="red" text={t('ViewShops.缺失买家消息数据')} />
                  </LineText>
                  {platform !== 'TOKOPEDIA' ? (
                    <StatusButton type="link" onClick={() => handleReAuth(record, true, false)}>
                      {t('ViewShops.补齐数据')}
                    </StatusButton>
                  ) : null}
                </Space>
              );
            }

            if (erpAuthStatus === 'AUTH_EXPIRE' || erpAuthStatus === 'UN_AUTH') {
              return (
                <Space size={0} direction="vertical">
                  <LineText>
                    <Badge color="red" text={t('ViewShops.缺失订单商品数据')} />
                  </LineText>
                  {platform !== 'TOKOPEDIA' ? (
                    <StatusButton type="link" onClick={() => handleReAuth(record, false, true)}>
                      {t('ViewShops.补齐数据')}
                    </StatusButton>
                  ) : null}
                </Space>
              );
            }

            if (
              erpAuthStatus === 'AUTH_NO_PERMISSION_LOCAL_REGISTRATION' ||
              chatAuthStatus === 'AUTH_NO_PERMISSION_LOCAL_REGISTRATION'
            ) {
              return (
                <Space size={0} direction="vertical">
                  <LineText>
                    <Badge color="red" text={t('ViewShops.未完成Shopee注册')} />
                  </LineText>
                  <Button
                    type="link"
                    onClick={() => {
                      if (storeType === 'CROSS') {
                        openExternalURL(`https://seller.shopee.cn`, '__blank');
                      } else {
                        openExternalURL(
                          `https://seller.${toLowerCase(region)}.shopee.cn`,
                          '__blank',
                        );
                      }
                    }}
                  >
                    {t('ViewShops.打开卖家中心')}
                  </Button>
                </Space>
              );
            }

            if (missingPermission) {
              return (
                <div>
                  <Badge color="var(--fett-surface-warning-text)" text={t('异常')} />
                  <Popover
                    content={
                      <div>
                        <div>
                          {t('no_premission_store_tips', {
                            x: [
                              missingCsatPermission && t('店铺表现'),
                              missingProductReviewPermission && t('商品评价'),
                            ].reduce((res, cur) => {
                              return cur ? `${res}「${cur}」` : res;
                            }, ''),
                          })}
                        </div>
                      </div>
                    }
                  >
                    <InfoCircleOutlined className="ml-1 cursor-pointer !text-[var(--fett-text-quaternary)]" />
                  </Popover>
                </div>
              );
            }

            if (erpAuthStatus === 'AUTH' && chatAuthStatus === 'AUTH') {
              if (!isStoreInitFinish) {
                return (
                  <Space>
                    <LineText style={{ color: 'var(--ant-primary-color)' }}>
                      <SyncOutlined spin />
                      {t('ViewShops.col.auth.status.intial')}
                    </LineText>
                  </Space>
                );
              }
              const badge = (
                <LineText>
                  <Badge
                    color="var(--fett-scenario-success-text)"
                    text={t('ViewShops.col.auth.status.active')}
                  />
                </LineText>
              );
              return <div>{badge}</div>;
            }

            return (
              <LineText>
                <Badge color="red" text={t('ViewShops.col.auth.status.to.be.failed')} />
              </LineText>
            );
          },
        },
        ...(platform === 'ALIEXPRESS'
          ? [
              {
                key: 'csp',
                title: (
                  <div>
                    {t('aeoverview.CSP插件场景')}
                    <Tooltip title={t('aeoverview.cspPluginTips')}>
                      <What />
                    </Tooltip>
                  </div>
                ),
                width: 160,
                render: (_, record) => {
                  const {
                    closedCsps = [],
                    usedCspCount,
                    totalCspCount,
                    erpAuthStatus,
                  } = record as AeAdminStore;

                  if (!isAuthStatusValid(erpAuthStatus)) {
                    return '-';
                  }

                  const cspLabel = {
                    s_basic_reception: t('aeoverview.基础场景-基础接待'),
                    s_service_pannel: t('aeoverview.基础场景-客服面板工具'),
                    s_smart_reception: t('aeoverview.高阶场景-智能接待'),
                    s_auto_task: t('aeoverview.高阶场景-自动任务'),
                  };

                  return (
                    <CspWrapper>
                      {`${usedCspCount}/${totalCspCount}`}
                      {closedCsps.length > 0 && (
                        <Popover
                          content={
                            <CspContent>
                              <div>{t('aeoverview.CSPPluginAiServiceWarning')}</div>
                              <ul>
                                {closedCsps.map((csp) => {
                                  return <li key={csp}>{cspLabel[csp]}</li>;
                                })}
                              </ul>
                              <Button
                                type="link"
                                href="https://csp.aliexpress.com/apps/csp/im#/settings/OpenPlugins"
                                target="__blank"
                              >
                                {t('aeoverview.去速卖通CSP IM工作台设置')}
                              </Button>
                            </CspContent>
                          }
                        >
                          <ExclamationCircleFilled />
                        </Popover>
                      )}
                    </CspWrapper>
                  );
                },
              } as ColumnProps<AdminStore>,
            ]
          : []),
        //
        ...(AuthBeAboutToExpireFeature && platform !== 'TOKOPEDIA'
          ? [
              {
                // 失效时间
                key: 'expireTimeStamp',
                title: () => {
                  return (
                    <div className="flex items-center">
                      <div className="mr-[2px]">{t('expire_time')}</div>
                      {['LAZADA', 'SHOPEE'].includes(platform) && (
                        <Tooltip
                          title={
                            platform === 'LAZADA'
                              ? t('lazada_exception_table_tip_copy')
                              : platform === 'SHOPEE'
                              ? t('shopee_exception_table_tip_copy')
                              : ''
                          }
                        >
                          <QuestionCircleOutlined />
                        </Tooltip>
                      )}
                    </div>
                  );
                },
                dataIndex: 'expireTimeStamp',
                width: 160,
                render: (value, record) => {
                  const end = dayjs(Number(value));
                  const now = dayjs();
                  const diff = end.diff(now, 'days');

                  if (
                    !value ||
                    !['AUTH', 'AUTH_PROCESS', 'AUTH_BE_ABOUT_EXPIRE'].includes(record.authStatus)
                  ) {
                    return '-';
                  }

                  return (
                    <div>
                      {value && <LineText>{end.format('YYYY-MM-DD HH:mm:ss')}</LineText>}

                      {record.authStatus === 'AUTH_BE_ABOUT_EXPIRE' && (
                        <div className="text-[12px] leading-[18px] mt-[2px] text-[var(--fett-surface-error-text)]">
                          {t('incentive.x天后到期', { x: diff })}
                        </div>
                      )}
                    </div>
                  );
                },
              },
            ]
          : []),
        {
          title: t('ViewShops.col.auth.time'),
          dataIndex: 'authTimestamp',
          sorter: false,
          width: 160,
          render: (timestamp) => {
            return timestamp ? (
              <LineText>{dayjs(Number(timestamp)).format('YYYY-MM-DD HH:mm:ss')}</LineText>
            ) : (
              '-'
            );
          },
        },
        {
          title: t('操作'),
          dataIndex: 'id',
          fixed: 'right',
          width: 220,
          render: (_, record) => {
            const { id, erpAuthStatus } = record;

            const deleteBtn = renderDeleteBtn(id);

            if (platform === 'ALIEXPRESS') {
              if (isAuthStatusValid(erpAuthStatus)) {
                return deleteBtn;
              }

              return (
                <>
                  <StatusButton
                    type="link"
                    style={{ paddingLeft: 0 }}
                    onClick={() => {
                      return getAliExpressAuthUrl()
                        .then((url) => openExternalURL(url, '__blank'))
                        .catch(() => {
                          message.error(t('authShop.获取授权链接失败'));
                        });
                    }}
                  >
                    {t('ViewShops.col.actions.continue')}
                  </StatusButton>
                  {deleteBtn}
                </>
              );
            }

            const goAuthBtn = (
              <>
                <TextButton onClick={() => handleReAuth(record)} type="link">
                  {t('ViewShops.col.actions.continue')}
                </TextButton>
              </>
            );

            return (
              <Space>
                {renderUpdateAccountBtn(record)}
                {goAuthBtn}
                {deleteBtn}
              </Space>
            );
          },
        },
      ] as ColumnProps<AdminStore>[]),
    );

    return columns;
  }, [
    t,
    platform,
    renderDeleteBtn,
    renderUpdateAccountBtn,
    appProductNameT,
    refreshStoreName,
    getRegionLabel,
    handleReAuth,
    authStatusParam,
  ]);

  // 表格搜索按钮事件
  const handleKeywordSearch = useCallback(
    async (value) => {
      setKeyword(value);
      setQueryAdminStores({
        keyWord: value,
        externalType: platform as any,
        region: currentRegion,
        pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
        page: 1,
        authStatus:
          (authStatusParamRef.current === -1 ? undefined : authStatusParamRef.current) ||
          listAuthStatusComputed,
      });
    },
    [
      currentRegion,
      listAuthStatusComputed,
      pagination.pageSize,
      platform,
      setQueryAdminStores,
      authStatusParamRef,
    ],
  );

  const isInitial =
    memorizedCount.LAZADA.total +
      memorizedCount.SHOPEE.total +
      memorizedCount.TOKOPEDIA.total +
      memorizedCount.ALIEXPRESS.total +
      memorizedCount.MERCADO.total +
      memorizedCount.KWAI.total +
      memorizedCount.FACEBOOK.total +
      memorizedCount.JOLECT.total +
      memorizedCount.TIKTOK_US.total +
      memorizedCount.TIKTOK.total <=
    0;

  const expandedRowRender = useMemo(
    () => (record: AdminStore) => {
      const { externalType } = record;
      const boolText = (value: boolean) => (value ? t('ViewShops.是') : t('ViewShops.否'));
      const columns = [
        {
          title: t('ViewShops.店铺类型'),
          dataIndex: 'storeType',
          key: 'storeType',
          render(storeType: IM_API.Admin.StoreType) {
            return <span>{t(`ViewShops.${storeType}`)}</span>;
          },
        },
        {
          title: t('ViewShops.是否SIP店铺'),
          dataIndex: 'isSip',
          key: 'isSip',
          render(value: boolean) {
            return boolText(value);
          },
        },
        {
          title: t('ViewShops.是否主店'),
          dataIndex: 'isPrimarySip',
          key: 'isPrimarySip',
          render(value: boolean) {
            return boolText(value);
          },
        },
        {
          title: t('ViewShops.授权帐号'),
          dataIndex: 'authAccount',
          key: 'authAccount',
          render(value: string) {
            return value || '-';
          },
        },
        {
          title: t('ViewShops.帐号体系'),
          dataIndex: 'authAccountSystem',
          key: 'authAccountSystem',
          render(value: IM_API.Admin.AuthAccountSystem) {
            return <span>{t(`ViewShops.${value}`, '-')}</span>;
          },
        },
      ].filter((x) => {
        if (externalType === 'SHOPEE') {
          return true;
        }
        return ['id', 'storeType', 'authAccount'].includes(x.dataIndex);
      });

      return <Table columns={columns} dataSource={[record]} pagination={false} />;
    },
    [t],
  );

  const onSelectedDeleteKeysChange = useCallback((newKeys: React.Key[]) => {
    setSelectedDeleteKeys(newKeys);
  }, []);

  const hasDeleteKeys = useMemo(() => selectedDeleteKeys.length > 0, [selectedDeleteKeys]);

  const handleDeleteKeysClick = useCallback(() => {
    Modal.confirm({
      title: t('ViewShops.batchDeleteConfirmText', { total: selectedDeleteKeys.length }),
      content: appProductNameT('ViewShops.batchDeleteConfirmDetail'),
      onOk() {
        return new Promise((resolve, reject) => {
          deleteStores({
            storeIds: selectedDeleteKeys as number[],
          })
            .then(() => {
              resolve(undefined);
              setSelectedDeleteKeys([]);
              refreshTable(pagination.current);
              refreshInProcessState();
            })
            .catch(() => {
              message.error(t('ViewShops.col.actions.remove.fail'));
              reject();
            });
        });
      },
    });
  }, [t, selectedDeleteKeys, refreshTable, pagination, appProductNameT]);

  useUpdateEffect(() => {
    setAuthStatusParam(Number(authStatusFromQuery) || -1);
    refreshTable(1, authStatusFromQuery);
  }, [authStatusFromQuery, refreshTable]);
  useUpdateEffect(() => {
    if (!authStatusFromQuery) {
      setAuthStatusParam(-1);
    }
  }, [platform]);

  const exportIcon = useMemo(() => {
    return (
      <Tooltip title={t('ViewShops.导出店铺列表')}>
        {exportStoreListLoading ? (
          <LoadingOutlined />
        ) : (
          <Button
            type="text"
            icon={<DownloadOutlined />}
            disabled={!stores.length}
            loading={exportStoreListLoading}
            onClick={() => {
              onDownload(() =>
                exportStoreList({
                  externalType: platform,
                  region: currentRegion,
                  keyWord: keyword,
                  authStatus:
                    (authStatusParam === -1 ? undefined : authStatusParam) ||
                    listAuthStatusComputed,
                }),
              );
            }}
          ></Button>
        )}
      </Tooltip>
    );
  }, [
    t,
    onDownload,
    authStatusParam,
    listAuthStatusComputed,
    keyword,
    currentRegion,
    platform,
    exportStoreListLoading,
    stores,
  ]);

  const exportButton = useMemo(
    () => (
      <Button
        type="text"
        icon={<DownloadOutlined />}
        disabled={!stores.length}
        loading={exportStoreListLoading}
        onClick={() => {
          onDownload(() =>
            exportStoreList({
              externalType: platform,
              region: currentRegion,
              keyWord: keyword,
              authStatus:
                (authStatusParam === -1 ? undefined : authStatusParam) || listAuthStatusComputed,
            }),
          );
        }}
      >
        {t('ViewShops.导出店铺列表')}
      </Button>
    ),
    [
      t,
      onDownload,
      authStatusParam,
      listAuthStatusComputed,
      keyword,
      currentRegion,
      platform,
      exportStoreListLoading,
      stores,
    ],
  );

  const table = useMemo(() => {
    if (platform === 'WA_BIZ') {
      return <WhatsappList total={memorizedCount.WA_BIZ.total} />;
    }

    const addShopPermission = getPermission('店铺管理');
    const shouldShowAddShop = addShopPermission && !['ALIEXPRESS', 'TOKOPEDIA'].includes(platform);

    const AddShopUI = shouldShowAddShop ? (
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => {
          switch (platform) {
            case 'SHOPEE': {
              setShopeeAuthShopDialogVisible(true);
              break;
            }
            case 'LAZADA': {
              setLazadaAuthShopDialogVisible(true);
              break;
            }
            case 'MERCADO': {
              setMercadoAuthShopDialogVisible(true);
              break;
            }
            case 'KWAI': {
              setKwaiAuthDialogVisible(true);
              break;
            }
            case 'FACEBOOK': {
              setFacebookDialogVisible(true);
              break;
            }
            case 'JOLECT': {
              setJolectAuthDialogVisible(true);
              break;
            }
            case 'TIKTOK_US':
            case 'TIKTOK': {
              setTiktokAuthShopDialogVisible(true);
              break;
            }
            default:
              break;
          }
        }}
      >
        {platform === 'FACEBOOK' ? t('添加公共主页') : t('ViewShops.添加授权店铺')}
      </Button>
    ) : (
      <></>
    );

    const authStatusSelect = AuthBeAboutToExpireFeature ? (
      <Radio.Group
        value={authStatusParam}
        onChange={(e) => {
          const value = e.target.value;
          setAuthStatusParam(value);
          refreshTable(1, value === -1 ? undefined : value);
        }}
      >
        <Radio.Button value={-1}>
          {t('status.ALL')}({countInfo?.totalCount || 0})
        </Radio.Button>
        {platform === 'TIKTOK' && (
          <Radio.Button value={3}>
            {t('异常')} ({errorCount.tiktokPermissionExceptionNum})
          </Radio.Button>
        )}
        {platform === 'TIKTOK_US' && (
          <Radio.Button value={3}>
            {t('异常')} ({errorCount.tiktokUsPermissionExceptionNum})
          </Radio.Button>
        )}
        {platform !== 'TOKOPEDIA' && (
          <Radio.Button value={2}>
            {t('be_about_to_expire')}({countInfo?.beAboutToExpireCount || 0})
          </Radio.Button>
        )}
        <Radio.Button value={1}>
          {t('ViewAppCenter.已失效')}({countInfo?.expiredCount || 0})
        </Radio.Button>
      </Radio.Group>
    ) : (
      <div />
    );

    const shouldShowBatchAddShop = SHOPEE_BATCH_AUTH ? ['SHOPEE', 'LAZADA'] : ['LAZADA'];

    return (
      <SectionTable
        title={t('ViewShops.shop.table.title')}
        extra={
          <Extra>
            {shouldShowBatchAddShop.includes(platform) &&
            getPermission('店铺管理') &&
            !isMasterOnline &&
            !isSiteGiant ? (
              <Button
                onClick={() =>
                  detectClient() &&
                  startPlatformAuth(platform.toLocaleLowerCase() as General.Platform)
                }
              >
                {t('ViewShops.批量添加店铺')}
              </Button>
            ) : null}
            {lazadaMultiVisible && (
              <LazadaMultiAuthModal
                visible={lazadaMultiVisible}
                goSingle={() => setLazadaAuthShopDialogVisible(true)}
                onCancel={(refresh: any) => {
                  hideLazadaMulti();
                  refresh && refreshTable();
                }}
              />
            )}
            {AddShopUI}
          </Extra>
        }
      >
        {platform === 'TOKOPEDIA' ? (
          <TokopediaEmpty />
        ) : (
          <>
            {platform === 'SHOPEE' &&
            listAuthStatusComputed &&
            errorCount.shopeeLocalRegisterExceptionNum ? (
              <Alert
                showIcon
                type="error"
                message={
                  <>
                    {t(
                      'ViewShops.当前有n家本土店铺未完成Shopee注册流程，影响店铺消息收发，请尽快前往卖家中心完善注册信息',
                      { count: errorCount.shopeeLocalRegisterExceptionNum },
                    )}
                    <Tooltip title={t('notificationDialog.p2')}>
                      <QuestionCircleOutlined />
                    </Tooltip>
                  </>
                }
              />
            ) : null}
            {listAuthStatusComputed || newPermissionEnable ? null : null}
            <SectionTableContent>
              <BatchUpdateAccountInfo onClose={refreshTable} />
              <FlexBetween>
                {hasDeleteKeys ? (
                  <Space align="center">
                    <DeleteKeysTotal>
                      {t('voucher.已选x', { x: selectedDeleteKeys.length })}
                    </DeleteKeysTotal>
                    <Button onClick={handleDeleteKeysClick}>{t('ViewShops.batchDeleteBtn')}</Button>
                  </Space>
                ) : (
                  authStatusSelect
                )}
                <Space>
                  <Actions>
                    {!AuthBeAboutToExpireFeature && (
                      <Checkbox
                        checked={listAuthStatusComputed === 1}
                        onChange={(e) => {
                          const listAuthStatus = e.target.checked ? 1 : undefined;
                          nav({
                            search: `?${simpleStringify({
                              tabName,
                              listAuthStatus,
                            })}`,
                          });
                        }}
                      >
                        {t('ViewShops.shopee.table.show.error.shops')}
                      </Checkbox>
                    )}
                    <Divider type="vertical" />
                    <SyncStoreData
                      externalType={platform}
                      region={currentRegion}
                      refresh={() => {
                        refreshTable();
                      }}
                    />
                    <Divider type="vertical" />
                    {exportIcon}
                    <Divider type="vertical" />
                    <Dropdown
                      className="mx-[12px]"
                      overlay={
                        <Menu>
                          <Menu.Item>
                            <BatchUpdateAliasModal
                              exportButton={exportButton}
                              refresh={batchUpdateAliasRefresh}
                            />
                          </Menu.Item>
                          <Menu.Item>
                            <ShopAliasSetting
                              platform={platform}
                              regionList={memorizedCount[platform].array.map((x) => x.region)}
                            />
                          </Menu.Item>
                        </Menu>
                      }
                    >
                      <div className="cursor-pointer">
                        <Icon
                          style={{ fontSize: 16 }}
                          component={AliasSettingIcon}
                          className="mr-[6px]"
                        />
                        {t('别名设置')}
                        <DownOutlined className="ml-[6px]" />
                      </div>
                    </Dropdown>
                  </Actions>
                  <Input.Search
                    prefix={<SearchOutlined />}
                    placeholder={t('ViewShops.shop.table.search.placeholder')}
                    enterButton={t('ViewShops.shop.table.search.btn.text')}
                    value={keyword}
                    onSearch={handleKeywordSearch}
                    onChange={(e) => {
                      setKeyword(e.target.value);
                      if (e.target.value === '') {
                        handleKeywordSearch('');
                      }
                    }}
                  />
                </Space>
              </FlexBetween>
              <Table
                rowKey="id"
                loading={loading}
                dataSource={stores}
                columns={columns}
                rowClassName={(record: AdminStore) => {
                  const { chatAuthStatus, erpAuthStatus, isStoreInitFinish } = record;
                  return isAuthStatusValid(chatAuthStatus) &&
                    isAuthStatusValid(erpAuthStatus) &&
                    !isStoreInitFinish
                    ? 'store-table-cell-init-loading'
                    : '';
                }}
                pagination={{
                  ...pagination,
                  showQuickJumper: true,
                  showSizeChanger: true,
                }}
                onChange={({ pageSize, current }, _, sorter) => {
                  // @ts-ignore
                  const order = sorter.order as SortOrder;
                  setPagination({
                    ...pagination,
                    current,
                    pageSize,
                  });
                  setQueryAdminStores({
                    keyWord: keyword,
                    externalType: platform,
                    page: current || 1,
                    region: currentRegion,
                    pageSize,
                    order: order === 'descend',
                    authStatus:
                      (authStatusParam === -1 ? undefined : authStatusParam) ||
                      listAuthStatusComputed,
                  });
                }}
                expandable={
                  platform !== 'TOKOPEDIA' && platform !== 'ALIEXPRESS'
                    ? { expandedRowRender }
                    : undefined
                }
                scroll={{ x: 'max-content' }}
                rowSelection={{
                  type: 'checkbox',
                  selectedRowKeys: selectedDeleteKeys,
                  onChange: onSelectedDeleteKeysChange,
                }}
              />
            </SectionTableContent>
          </>
        )}
      </SectionTable>
    );
  }, [
    platform,
    getPermission,
    t,
    authStatusParam,
    countInfo?.totalCount,
    countInfo?.beAboutToExpireCount,
    countInfo?.expiredCount,
    SHOPEE_BATCH_AUTH,
    lazadaMultiVisible,
    listAuthStatusComputed,
    errorCount.shopeeLocalRegisterExceptionNum,
    newPermissionEnable,
    refreshTable,
    hasDeleteKeys,
    selectedDeleteKeys,
    handleDeleteKeysClick,
    currentRegion,
    exportIcon,
    exportButton,
    batchUpdateAliasRefresh,
    memorizedCount,
    keyword,
    handleKeywordSearch,
    loading,
    stores,
    columns,
    pagination,
    expandedRowRender,
    onSelectedDeleteKeysChange,
    detectClient,
    startPlatformAuth,
    hideLazadaMulti,
    nav,
    tabName,
    setQueryAdminStores,
    errorCount.tiktokPermissionExceptionNum,
    errorCount.tiktokUsPermissionExceptionNum,
  ]);

  const lazadaAuthModal = useMemo(
    () => (
      <LazadaAuthModal
        onResultConfirm={() => {
          setPlatform('LAZADA');
          setForceUpdate((a) => a + 1);
          refreshTable();
        }}
        ref={lazadaAuthModalRef}
      />
    ),
    [refreshTable],
  );

  useCallback(() => {
    if (
      shopeeAuthDataRef.current.authMode === AuthMode.Single &&
      authStatus === AuthShopsStatus.Complete
    ) {
      hideStoreTypeModal();
    }
  }, [authStatus, hideStoreTypeModal]);

  // 开始授权
  const startMultiAuth = useCallback(
    async (info: Omit<Chattt.StartAuthPayload, 'token'>, quickMode: boolean = false) => {
      if (chatttSDK?.shopee.multiAuth) {
        const { storeType, shops, remember, departmentIds, proxyMode } = info;
        reset();
        const isInDisabledList = payload?.orgId && authProxyDisabledList.includes(payload?.orgId);
        const authProxyDisabled = isInDisabledList || !proxyMode;
        const taskId = await chatttSDK.shopee.multiAuth('start', {
          authProxyDisabled,
          token: token as unknown as string,
          storeType,
          shops,
          remember,
          quickMode,
          departmentIds,
        });

        setShopeeAuthShopsTaskId(taskId);
        setShopeeAuthShopsMode(shopeeAuthDataRef.current.authMode);

        if (shopeeAuthDataRef.current.authMode === AuthMode.Multi) {
          hideStoreTypeModal();
        }
      }
    },
    [
      authProxyDisabledList,
      hideStoreTypeModal,
      payload?.orgId,
      setShopeeAuthShopsMode,
      setShopeeAuthShopsTaskId,
      token,
      reset,
    ],
  );

  // 快速模式授权
  const authConfirm = useCallback(
    async (payload: Omit<Chattt.StartAuthPayload, 'token'>) => {
      const quickMode = await canQuickMode(payload.shops);

      if (quickMode) {
        const confirm = Modal.confirm({
          title: t('ViewShops.shopee.multi.interrupt.modal.confirm.title'),
          closable: true,
          closeIcon: <CloseOutlined />,
          content: (
            <>
              <Text>{t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.question')}</Text>
              <Text>{t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.question.tips')}</Text>
            </>
          ),
          cancelText: t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.normal'),
          okText: t('ViewShops.shopee.multi.interrupt.modal.confirm.btn.quick'),
          cancelButtonProps: {
            onClick: () => {
              setIsQuickMode(false);
              startMultiAuth(payload, false);
              confirm.destroy();
            },
          },
          onOk() {
            setIsQuickMode(true);
            startMultiAuth(payload, true);
          },
        });
      } else {
        setIsQuickMode(false);
        startMultiAuth(payload, false);
      }
    },
    [canQuickMode, setIsQuickMode, startMultiAuth, t],
  );

  const closeShopeeAuthShopDialog = useCallback(() => {
    setShopeeAuthPartnerType({
      original: true,
      shopName: '',
      chat: true,
      erp: true,
    });
    setShopeeInitialValues(undefined);
    setShopeeAuthShopDialogVisible(false);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setShopeeAuthPartnerType, setShopeeInitialValues]);

  const closeLazadaAuthShopDialog = useCallback(() => {
    setLazadaAuthShopDialogVisible(false);
    setGuideInitialValues(undefined);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setGuideInitialValues]);

  const closeMercadoAuthShopDialog = useCallback(() => {
    setMercadoAuthShopDialogVisible(false);
    setGuideInitialValues(undefined);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setGuideInitialValues]);

  const closeKwaiAuthShopDialog = useCallback(() => {
    setKwaiAuthDialogVisible(false);
    setGuideInitialValues(undefined);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setGuideInitialValues]);

  const closeFacebookAuthShopDialog = useCallback(() => {
    setFacebookDialogVisible(false);
    setGuideInitialValues(undefined);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setGuideInitialValues]);

  const closeJolectAuthShopDialog = useCallback(() => {
    setJolectAuthDialogVisible(false);
    refreshTable();
  }, [refreshTable]);

  const closeTiktokAuthShopDialog = useCallback(() => {
    setTiktokAuthShopDialogVisible(false);
    setGuideInitialValues(undefined);
    refreshTable(1, authStatusParam);
  }, [refreshTable, authStatusParam, setGuideInitialValues]);

  const platformList: PlatformSelectorProps['platformList'] = useMemo(() => {
    const list: PlatformSelectorProps['platformList'] = [
      'SHOPEE',
      'LAZADA',
      'MERCADO',
      'KWAI',
      'JOLECT',
      'TIKTOK',
    ];
    if (tiktokUSEnable) {
      list.push('TIKTOK_US');
    }
    if (waEnable) {
      list.push('WA_BIZ' as any);
    }
    list.push('FACEBOOK');
    list.push('TOKOPEDIA');
    return list;
  }, [waEnable, tiktokUSEnable]);

  useEffect(() => {
    if (!mounted || isFinishNewbieAuth) return;
    refreshTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFinishNewbieAuth]);

  useEffect(() => {
    if (!mounted) return;
    refreshInvalidAccountList();
    regionCountRefresher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  useEffect(() => {
    if (!mounted) return;
    refreshTable(1, listAuthStatusComputed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listAuthStatusComputed]);

  useEffect(() => {
    setSelectedDeleteKeys([]);
  }, [platform, currentRegion]);

  useMount(() => {
    const keyWord = searchParams.get('storeName');
    setAuthStatusParam(Number(authStatusFromQuery) || -1);
    refreshTable(1, Number(authStatusFromQuery), keyWord || '');
    setMounted(true);
  });

  return (
    <Container>
      <>
        {lazadaAuthModal}
        {storeTypeModalVisible && (
          <StoreTypeModal
            isInitial={isInitial}
            onChange={(
              authMode,
              storeType,
              region,
              multiShops,
              remember,
              departmentIds,
              proxyMode,
            ) => {
              console.log(
                'onChange',
                authMode,
                storeType,
                region,
                multiShops,
                remember,
                departmentIds,
                proxyMode,
              );
              shopeeAuthDataRef.current.authMode = authMode;
              shopeeAuthDataRef.current.storeType = storeType;
              shopeeAuthDataRef.current.region = region;
              shopeeAuthDataRef.current.multiShops = multiShops || [];
              shopeeAuthDataRef.current.remember = remember;
              shopeeAuthDataRef.current.departmentIds = departmentIds;
              shopeeAuthDataRef.current.proxyMode = proxyMode;
            }}
            onOk={async (_, isQuickMode) => {
              if (!multiAuthSupportSelectDepartment) {
                return message.warning({
                  content: t('ViewShops.shopee.auth.upgrade'),
                });
              }
              if (shopeeAuthDataRef.current.storeType === 'UNKNOWN') {
                return;
              }

              const multiAuthPayload = {
                storeType: shopeeAuthDataRef.current.storeType,
                shops: shopeeAuthDataRef.current.multiShops,
                remember: shopeeAuthDataRef.current.remember,
                departmentIds: shopeeAuthDataRef.current.departmentIds,
                proxyMode: shopeeAuthDataRef.current.proxyMode,
              };

              if (isQuickMode) {
                setIsQuickMode(true);
                startMultiAuth(multiAuthPayload, true);
              } else {
                setIsQuickMode(false);
                startMultiAuth(multiAuthPayload, false);
              }
            }}
            visible={storeTypeModalVisible}
            onCancel={hideStoreTypeModal}
            initialValues={{
              storeType: shopeeAuthDataRef.current.storeType,
              region: shopeeAuthDataRef.current.region,
              authMode: shopeeAuthDataRef.current.authMode,
            }}
          />
        )}
        <MultiAuthInterruptModal
          visible={showRPAProgressModal}
          onConfirm={(continueUpload = false) => {
            setForceUpdate((a) => a + 1);
            setShowRPAProgreessModal(false);
            if (continueUpload) {
              showStoreTypeModal();
            }
          }}
        />
        {
          <>
            <Body>
              {!isAeProduct && (
                <SectionHeader
                  title={
                    <PlatformSelector
                      activePlatform={(platform || globalQuery.platform) as any}
                      onActivePlatformChange={changePlatform}
                      platformList={platformList}
                      platformRender={(value, label) => (
                        <Badge
                          status="warning"
                          className={platform !== value ? 'unactive' : ''}
                          dot={
                            errorCount?.[
                              `${
                                value === 'ALIEXPRESS'
                                  ? 'aliExpress'
                                  : value === 'TIKTOK_US'
                                  ? 'tiktokUs'
                                  : toLowerCase(value)
                              }AuthExceptionNum` as const
                            ] > 0
                          }
                        >
                          {value === 'TIKTOK_US' ? t('tiktok_us') : label}(
                          {memorizedCount?.[value]?.total})
                        </Badge>
                      )}
                    />
                  }
                >
                  {platform === 'WA_BIZ' ? null : (
                    <RegionSelector
                      activeRegion={currentRegion || 'ALL'}
                      regionList={
                        platform === 'FACEBOOK' || platform === 'TOKOPEDIA'
                          ? ['ALL']
                          : memorizedCount[platform].array
                              .map((x: any) => toUpperCase(x.region))
                              .concat(['ALL'])
                      }
                      regionRender={(region, label) =>
                        `${label}(${
                          region === 'ALL'
                            ? memorizedCount[platform].total
                            : memorizedCount[platform].array.find(
                                (x) => region === toUpperCase(x.region),
                              )?.count
                        })`
                      }
                      onActiveRegionChange={(region) => {
                        const next = region === 'ALL' ? undefined : region;
                        setCurrentRegion(next);
                        setGlobalQuery((pre) => ({
                          ...pre,
                          region: next,
                        }));
                        setQueryAdminStores({
                          externalType: platform,
                          region: next,
                          pageSize: pagination.pageSize || DEFAULT_PAGE_SIZE,
                          page: 1,
                          authStatus: listAuthStatusComputed,
                        });
                      }}
                    />
                  )}
                </SectionHeader>
              )}
              {table}
            </Body>
          </>
        }
        <SingleAuthResultModal onCancel={() => refreshTable()} />
        <AuthCaptchaModal />
        <MultipleAuthProgressModal />
        {isMultipleAuthResultCompleted ? (
          <MultipleAuthResultModal
            visible={isMultipleAuthResultCompleted}
            onCancel={() => refreshTable()}
            onFailedRetry={(failData) => {
              if (shopeeAuthDataRef.current.storeType === 'UNKNOWN') {
                return;
              }

              authConfirm({
                storeType: shopeeAuthDataRef.current.storeType,
                remember: shopeeAuthDataRef.current.remember,
                shops: failData.map((fail) => ({
                  region: fail.region as RPA.Shopee.ShopeeMultiAuthShopRegion,
                  accountName: fail.account,
                  password: fail.password,
                  isCnsc: fail.isSubaccount,
                  shopName: fail.shopNames,
                })),
              });
            }}
            onFailedExport={async (failedList) => {
              const { storeType } = shopeeAuthDataRef.current;
              if (storeType === 'UNKNOWN') return;
              let fileURL;
              const isLocal = storeType === 'LOCAL';
              if (isLocal) {
                fileURL = getShopeeLocalMultipleAuthTemplateFile(systemLanguage);
              } else {
                fileURL = getShopeeCrossMultipleAuthTemplateFile(systemLanguage);
              }
              if (fileURL) {
                const bufferData = await (await fetch(fileURL)).arrayBuffer();
                const workbook = xlsx.read(bufferData);
                const ws = workbook.Sheets[workbook.SheetNames[0]];
                if (isLocal && !ws.E3) {
                  ws.E3 = {
                    t: 's',
                    v: '失败原因',
                  } as xlsx.CellObject;
                }
                if (!isLocal && !ws.F3) {
                  ws.F3 = {
                    t: 's',
                    v: '失败原因',
                  } as xlsx.CellObject;
                }
                const jsonData = failedList.map((item) => {
                  const desc = getAuthDescription(item);
                  const reason = `${
                    desc.title || t('ViewShops.shopee.multi.auth.errors.fallback')
                  }(${desc.subTitle || ''})`;

                  if (isLocal) {
                    return {
                      region: regionLabelMap[item.info.region.toLowerCase() as General.Region],
                      account: item.info.account,
                      password: '',
                      shop: item.info.shopNames,
                      reason,
                    };
                  }
                  return {
                    system: item.info.isSubaccount ? 'CNSC' : '普通店铺帐号',
                    region: item.info.isSubaccount
                      ? 'CNSC'
                      : regionLabelMap[item.info.region.toLowerCase() as General.Region],
                    account: item.info.account,
                    password: '',
                    shop: item.info.shopNames,
                    reason,
                  };
                });
                xlsx.utils.sheet_add_json(ws, jsonData, {
                  skipHeader: true,
                  origin: 'A4',
                  header: [],
                });
                xlsx.writeFile(workbook, isLocal ? '本土授权失败.xlsx' : '跨境授权失败.xlsx');
                message.success('导出成功');
              }
            }}
            isConfigService={false}
            onConfigService={noop}
          />
        ) : null}
      </>
      {meta && (
        <UpdateAccountModal
          list={meta}
          refresh={() => {
            refreshTable();
            refreshInvalidAccountList();
            hide();
          }}
          visible={visible}
          mode="single"
          onCancel={() => hide()}
        />
      )}
      {shopeeAuthShopDialogVisible ? (
        <ShopeeAuthShopDialog
          visible={shopeeAuthShopDialogVisible}
          original={shopeeAuthPartnerType?.original}
          shopName={shopeeAuthPartnerType?.shopName}
          chatPartnerType={shopeeAuthPartnerType?.chat}
          erpPartnerType={shopeeAuthPartnerType?.erp}
          initialStoreType={shopeeInitialValues?.storeType}
          initialRegion={shopeeInitialValues?.region}
          initialDepartmentIds={shopeeInitialValues?.departmentIds}
          onCancel={closeShopeeAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeShopeeAuthShopDialog();
          }}
        />
      ) : null}
      {lazadaAuthShopDialogVisible ? (
        <LazadaAuthShopDialog
          visible={lazadaAuthShopDialogVisible}
          initialStoreType={guideInitialValues?.storeType}
          initialRegion={guideInitialValues?.region}
          initialDepartmentIds={guideInitialValues?.departmentIds}
          onCancel={closeLazadaAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeLazadaAuthShopDialog();
          }}
          closable
        />
      ) : null}
      {tiktokAuthShopDialogVisible ? (
        <TiktokAuthShopDialog
          platform={platform}
          visible={tiktokAuthShopDialogVisible}
          initialStoreType={guideInitialValues?.storeType}
          initialRegion={guideInitialValues?.region}
          initialDepartmentIds={guideInitialValues?.departmentIds}
          onCancel={closeTiktokAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeTiktokAuthShopDialog();
          }}
          closable
        />
      ) : null}
      {mercadoAuthShopDialogVisible ? (
        <MercadoAuthShopDialog
          visible={mercadoAuthShopDialogVisible}
          initialStoreType={guideInitialValues?.storeType}
          initialRegion={guideInitialValues?.region}
          initialDepartmentIds={guideInitialValues?.departmentIds}
          onCancel={closeMercadoAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeMercadoAuthShopDialog();
          }}
          closable
        />
      ) : null}
      {kwaiAuthDialogVisible && (
        <KwaiAuthShopDialog
          visible={kwaiAuthDialogVisible}
          initialStoreType={guideInitialValues?.storeType}
          initialRegion={guideInitialValues?.region}
          initialDepartmentIds={guideInitialValues?.departmentIds}
          onCancel={closeKwaiAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeKwaiAuthShopDialog();
          }}
          closable
        />
      )}
      {facebookDialogVisible && (
        <GuideDialog
          defaultPlatform="facebook"
          defaultTitle={t('添加公共主页')}
          initialStoreType={guideInitialValues?.storeType}
          initialRegion={guideInitialValues?.region}
          initialDepartmentIds={guideInitialValues?.departmentIds}
          closable
          maskClosable={false}
          visible={facebookDialogVisible}
          onCancel={closeFacebookAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeFacebookAuthShopDialog();
          }}
        />
      )}
      {jolectAuthDialogVisible && (
        <JolectAuthShopDialog
          visible={jolectAuthDialogVisible}
          onCancel={closeJolectAuthShopDialog}
          isConfigService={isOverOneAssistant}
          onConfigService={() => {
            closeJolectAuthShopDialog();
          }}
          closable
        />
      )}
      {editTokoShopModalVisible && (
        <EditShopModal
          title={
            editTokoShopModalMeta?.initialValues?.storeName
              ? t('ViewShops.编辑店铺')
              : t('ViewShops.添加授权店铺')
          }
          visible={editTokoShopModalVisible}
          onDone={() => updateTokoShopModalVisible(false, { initialValues: {} })}
          onCancel={() => updateTokoShopModalVisible(false, { initialValues: {} })}
          initialValues={editTokoShopModalMeta?.initialValues}
          onSuccess={() => refreshTable(pagination.current)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  overflow-y: auto;
`;
const Section = styled.section`
  position: relative;
  background-color: var(--fett-bg-panel);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1);
  margin: 12px 0;
`;
const SectionHeader = styled(Card)`
  margin: 0 0 16px 0;
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  color: var(--fett-text-primary);
  .ant-card-head {
    padding-left: 0;
    padding-right: 0;
    .unactive {
      opacity: 0.5;
    }
    .ant-card-head-title {
      padding: 0;
    }
  }
  .ant-card-body {
    display: flex;
    padding: 0;
    background: var(--theme-fill-6);
    width: 100%;
  }
`;
const SectionTable = styled(Card)`
  background: var(--fett-bg-panel);
  border-color: var(--fett-border-default);
  color: var(--fett-text-primary);
  .ant-card-head {
    border-bottom-color: var(--fett-border-default);
  }
  .ant-card-head-title,
  .ant-card-extra {
    color: var(--fett-text-primary);
  }
  .ant-card-body {
    position: relative;
    background: var(--fett-bg-panel);
  }
  .store-table-cell-init-loading {
  }
  .ant-card-extra {
    padding: 12px 0;
    flex: 1;
  }
  .ant-card-head-title {
    flex: none;
  }
  div.ant-typography {
    margin-bottom: 0;
  }
`;
const Body = styled.div`
  height: calc(100vh - var(--header-height) - var(--tabs-height) - 24px);
  padding: 16px;
  box-sizing: border-box;
  background-color: var(--fett-bg-app);
  color: var(--fett-text-primary);
  font-size: 16px;
  font-weight: normal;
  ${Section} {
    height: 100%;
    overflow-y: auto;
    flex: 0 0 100%;
  }
  table > thead {
    /* position: sticky;
    top: 108px;
    z-index: 9; */
  }
`;
const TextButton = styled(StatusButton)`
  padding-left: 0;
  padding-right: 0;
`;

const LineText = styled.span`
  word-break: keep-all;
  white-space: nowrap;
`;
const Extra = styled.div`
  display: flex;
  justify-content: right;
  gap: 16px;
  .ant-input-search {
    width: 290px;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    color: var(--fett-text-secondary);
    &:hover,
    &:active {
      color: var(--fett-app-primary-color);
      background-color: unset;
    }
    span + .anticon {
      margin-left: 0;
    }
  }
`;
const SectionTableContent = styled.div`
  margin: -24px;
  padding: 24px;
  background: var(--fett-bg-panel);
  .ant-radio-button-wrapper {
    background: var(--fett-bg-input);
    border-color: var(--fett-border-default);
    color: var(--fett-text-secondary);
  }
  .ant-radio-button-wrapper:not(:first-child)::before {
    background-color: var(--fett-border-default);
  }
  .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
    background: var(--fett-bg-active);
    color: var(--fett-app-primary-color);
    border-color: var(--fett-app-primary-color);
  }
  .ant-badge-status-text {
    color: var(--fett-text-secondary);
  }
  .ant-pagination {
    color: var(--fett-text-secondary);
  }
  .ant-pagination-item,
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link,
  .ant-pagination-options .ant-select-selector {
    background: var(--fett-bg-input);
    border-color: var(--fett-border-default);
    color: var(--fett-text-secondary);
  }
  .ant-pagination-item-active {
    background: var(--fett-bg-active);
    border-color: var(--fett-app-primary-color);
  }
`;
const FlexBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const DeleteKeysTotal = styled.span`
  color: var(--fett-text-secondary);
`;

const CspWrapper = styled.div`
  width: 160px;
  display: flex;
  align-items: center;
  > span {
    margin-left: 8px;
    color: orange;
    cursor: pointer;
  }
`;

const CspContent = styled.div`
  > div {
    font-weight: 500;
  }
  > ul {
    margin: 8px 0;
    padding: 0;
    list-style: none;
  }
  > a {
    padding-left: 0;
  }
`;

const styles = {
  column: css`
    white-space: pre-wrap;
  `,
};

export default withSuspense(Stores, { fallback: <LoadingOutlined /> });

const SyncStoreData = (props: {
  externalType: General.PlatformUpperCaseTunnel;
  region?: General.UpperCaseRegion;
  refresh?: () => void;
}) => {
  const { externalType, region = 'ALL', refresh } = props;
  const { t } = useTranslate();
  const [loading, setLoading] = useState(false);
  const { runAsync } = useRequest((data) => syncStoreInfo(data), {
    manual: true,
    onError: () => {
      message.error(t('translation.操作失败，请稍后再试'));
    },
  });
  useEffect(() => {
    return nimInst.on('sys:message:sync_shop_info', () => {
      setLoading(false);
      refresh?.();
    });
  }, [setLoading, refresh]);
  return (
    <Tooltip title={t('更新店铺数据')}>
      <Button
        onClick={() => {
          setLoading(true);
          const timer = setTimeout(() => {
            setLoading(false);
            clearTimeout(timer);
          }, 10 * 60 * 1000);
          runAsync({
            externalType,
            region,
          });
        }}
        type="text"
        icon={loading ? <LoadingOutlined /> : <ReloadOutlined />}
      ></Button>
    </Tooltip>
  );
};
